import React from 'react';
import { View, Text, Image, StyleSheet } from '@react-pdf/renderer';
import { StandardTitle } from './StandardTitle';

const styles = StyleSheet.create({
    container: {
        marginTop: 10,
        marginBottom: 20,
        paddingBottom: 10,
    },
    palletSection: {
        marginBottom: 20,
        breakInside: 'avoid',
    },
    palletHeading: {
        fontSize: 14,
        fontWeight: 'bold',
        marginBottom: 8,
        backgroundColor: '#f0f4f8',
        padding: 6,
        borderRadius: 3,
    },
    categoryHeading: {
        fontSize: 12,
        fontWeight: 'bold',
        marginTop: 10,
        marginBottom: 8,
        backgroundColor: '#f5f5f5',
        padding: 4,
        borderRadius: 2,
    },
    imagesGrid: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        marginLeft: -5,
        marginRight: -5,
    },
    imageContainer: {
        width: '33%',
        padding: 5,
        marginBottom: 15,
        minHeight: 120,
    },
    image: {
        width: '100%',
        height: 100,
        objectFit: 'contain',
        borderRadius: 3,
    },
    errorContainer: {
        width: '100%',
        height: 100,
        backgroundColor: '#f8d7da',
        borderRadius: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    errorText: {
        fontSize: 10,
        color: '#721c24',
        textAlign: 'center',
    },
    caption: {
        fontSize: 8,
        color: '#666',
        marginTop: 4,
        textAlign: 'center',
    },
    noImagesText: {
        fontSize: 10,
        color: '#777',
        fontStyle: 'italic',
        marginTop: 5,
        marginBottom: 10,
    },
    pageBreak: {
        height: 1,
        borderTop: '1pt solid white',
        marginTop: 30,
        marginBottom: 30,
        breakAfter: 'page',
    }
});

// Helper function to format category names for display
const formatCategoryName = (category: string): string => {
    const categoryMap: Record<string, string> = {
        'gpsRoute': 'GPS Route',
        'preship': 'Pre-Ship',
        'postship': 'Post-Ship',
        'stacking': 'Stacking',
        'unit-load-images': 'Unit Load Images'
    };

    return categoryMap[category] || category.charAt(0).toUpperCase() + category.slice(1);
};

interface MediaItem {
    id: number;
    url: string;
    localUrl?: string;
    type: string;
    palletId: number;
}

interface PalletType {
    id: number;
    name: string;
    variant: string;
    shippingModes?: Array<{ mode: number }>;
}

interface MediaPDFProps {
    selectedImages: MediaItem[];
    palletTypes: PalletType[];
}

// Component to render a single image or error placeholder
const MediaImage = ({ image, index, category }: { image: MediaItem; index: number; category: string }) => {
    // We'll wrap the image in a try-catch for PDF rendering
    try {
        return (
            <View style={styles.imageContainer}>
                <Image
                    src={image.localUrl || image.url}
                    style={styles.image}
                    cache
                />
                <Text style={styles.caption}>
                    {formatCategoryName(category)} Image {index + 1}
                </Text>
            </View>
        );
    } catch (error) {
        return (
            <View style={styles.imageContainer}>
                <View style={styles.errorContainer}>
                    <Text style={styles.errorText}>Image could not be loaded</Text>
                </View>
                <Text style={styles.caption}>
                    {formatCategoryName(category)} Image {index + 1} (Error)
                </Text>
            </View>
        );
    }
};

export const MediaPDF: React.FC<MediaPDFProps> = ({ selectedImages, palletTypes }) => {
    // Group images by pallet ID
    const imagesByPallet: Record<number, Record<string, MediaItem[]>> = {};

    // Initialize with all pallets so we show them even if they have no images
    palletTypes.forEach(pallet => {
        imagesByPallet[pallet.id] = {};
    });

    // Group images by pallet and category
    selectedImages.forEach(image => {
        if (!imagesByPallet[image.palletId]) {
            imagesByPallet[image.palletId] = {};
        }

        if (!imagesByPallet[image.palletId][image.type]) {
            imagesByPallet[image.palletId][image.type] = [];
        }

        imagesByPallet[image.palletId][image.type].push(image);
    });

    // Filter pallets with images
    const palletsWithImages = palletTypes.filter(pallet => {
        const palletImages = imagesByPallet[pallet.id];
        return Object.values(palletImages).some(images => images.length > 0);
    });

    // Determine if we need to insert page breaks
    // For larger collections, we'll limit to 2 pallets per page
    const insertPageBreaks = palletsWithImages.length > 2;

    return (
        <View style={styles.container}>
            <StandardTitle title="Project Images" />

            {palletsWithImages.map((pallet, palletIndex) => {
                const palletImages = imagesByPallet[pallet.id];
                const hasImages = Object.values(palletImages).some(images => images.length > 0);

                if (!hasImages) return null;

                // Count total images for this pallet
                const totalImages = Object.values(palletImages).reduce(
                    (sum, images) => sum + images.length, 0
                );

                // Add page break after every second pallet when there are many images
                const needsPageBreak = insertPageBreaks &&
                    palletIndex < palletsWithImages.length - 1 &&
                    (palletIndex + 1) % 2 === 0;

                return (
                    <React.Fragment key={pallet.id.toString()}>
                        <View style={styles.palletSection}>
                            <Text style={styles.palletHeading}>
                                {pallet.name} - {pallet.variant}
                                {/* {pallet.shippingModes && pallet.shippingModes.length > 0 &&
                                    ` (Mode ${pallet.shippingModes.map(sm => sm.mode).join(', ')})`
                                } */}
                            </Text>

                            {Object.entries(palletImages).map(([category, images]) => {
                                if (images.length === 0) return null;

                                // For many images in one category, limit to 6 per row across 2 rows (12 total)
                                // to prevent overflow 
                                const displayImages = images.length > 12 ? images.slice(0, 12) : images;
                                const hasMoreImages = images.length > 12;

                                return (
                                    <View key={`${pallet.id}-${category}`}>
                                        <Text style={styles.categoryHeading}>
                                            {formatCategoryName(category)}
                                            {hasMoreImages ? ` (Showing 12 of ${images.length})` : ''}
                                        </Text>

                                        <View style={styles.imagesGrid}>
                                            {displayImages.map((image, index) => (
                                                <MediaImage
                                                    key={`${image.id}-${index}`}
                                                    image={image}
                                                    index={index}
                                                    category={category}
                                                />
                                            ))}
                                        </View>
                                    </View>
                                );
                            })}
                        </View>

                        {/* Add page break if needed */}
                        {needsPageBreak && <View style={styles.pageBreak} />}
                    </React.Fragment>
                );
            })}

            {palletTypes.length > 0 && selectedImages.length === 0 && (
                <Text style={styles.noImagesText}>No images selected for this project.</Text>
            )}
        </View>
    );
}; 